import type { ColumnDef } from '@tanstack/react-table';
import type { Theme } from 'hephaestus/core';
import type { TFunction } from 'i18next';
import type {
  PortfolioReport,
  SubAccountReport,
  SubTradeRequest,
} from '../types';
import { calculateDrift, getHoldingAllocationPct } from '../utils';

type FormatMoneyFn = (cents: number) => string;

export type GeneralInfoRow = {
  label: string;
  value: string;
  color: string | undefined;
};

export type AccountBreakdownRow = {
  id: string;
  type: string;
  clientName: string;
  marketValueCents: number;
  cashAvailableForTradeCents: number;
  pendingWithdrawCents: number;
};

export type PendingTradesState = {
  pendingTrades: SubTradeRequest[];
  pendingTradesCalled: boolean;
  pendingTradesLoading: boolean;
};

export type EnrichedHolding = PortfolioReport['holdings'][number] & {
  currentPct: number;
  targetPct: number;
  driftPct: number;
  driftCents: number;
};

export const HOLDING_COLUMNS: ColumnDef<EnrichedHolding>[] = [
  {
    id: 'symbol',
    header: '',
    accessorFn: (r) => r.financialProduct?.ticker || r.currency || 'Cash',
  },
  {
    id: 'currency',
    header: '',
    accessorFn: (r) => r.currency || r.financialProduct?.currency || '',
  },
  {
    id: 'quantity',
    header: '',
    accessorFn: (r) => r.quantity || 0,
  },
  {
    id: 'price',
    header: '',
    accessorFn: (r) => r.financialProduct?.currentPriceCents || 0,
  },
  {
    id: 'value',
    header: '',
    accessorFn: (r) => r.valueCents || 0,
  },
  {
    id: 'curPct',
    header: '',
    accessorFn: (r) => r.currentPct,
  },
  {
    id: 'tgtPct',
    header: '',
    accessorFn: (r) => r.targetPct,
  },
  {
    id: 'driftPct',
    header: '',
    accessorFn: (r) => r.driftPct,
  },
  {
    id: 'driftVal',
    header: '',
    accessorFn: (r) => r.driftCents,
  },
];

export function enrichHoldings(report: PortfolioReport): EnrichedHolding[] {
  if ((report.holdings?.length ?? 0) === 0) return [];
  return report.holdings.map((h) => {
    const { currentPct, targetPct } = getHoldingAllocationPct(
      h,
      report.marketValueCents,
    );
    const driftPct = currentPct - targetPct;
    const driftCents = (h.valueCents || 0) - (h.expectedValueCents || 0);
    return { ...h, currentPct, targetPct, driftPct, driftCents };
  });
}

const mapSubAccountReportToBreakdownRow = (
  subAcct: SubAccountReport,
): AccountBreakdownRow => {
  const {
    marketValueCents,
    cashAvailableForTradeCents,
    pendingWithdrawCents = 0,
  } = subAcct;
  const { id, account } = subAcct.subAccount;
  const { type, user } = account;

  return {
    id,
    type,
    clientName: user.firstName || user.entityName || '—',
    marketValueCents,
    cashAvailableForTradeCents,
    pendingWithdrawCents,
  };
};

export function buildAccountBreakdownRows(
  report: PortfolioReport,
): AccountBreakdownRow[] {
  if (report.subAccounts && report.subAccounts.length > 0) {
    return report.subAccounts.map(mapSubAccountReportToBreakdownRow);
  }

  if (
    report.modelPortfolioType === 'SUB_ACCOUNT' &&
    report.object.__typename === 'SubAccount'
  ) {
    const obj = report.object;
    return [
      {
        id: obj.id,
        type: obj.account.type,
        clientName:
          obj.account.user.firstName || obj.account.user.entityName || '—',
        marketValueCents: report.marketValueCents,
        cashAvailableForTradeCents: report.cashAvailableForTradeCents,
        pendingWithdrawCents: 0,
      },
    ];
  }

  return [];
}

const classifyDriftSeverity = (
  driftPct: number,
  theme: Theme,
  t: TFunction,
): { label: string; color: string | undefined } => {
  if (driftPct > 5) {
    return {
      label: t('rowDetail.generalInfo.driftExceedsThreshold'),
      color: theme.color.negative,
    };
  }
  if (driftPct > 2) {
    return {
      label: t('rowDetail.generalInfo.driftMonitor'),
      color: theme.color.warning,
    };
  }
  return {
    label: t('rowDetail.generalInfo.driftWithinTolerance'),
    color: theme.color.positive,
  };
};

const getPendingTradesDisplay = (
  report: PortfolioReport,
  pendingTradesState: PendingTradesState,
  theme: Theme,
  t: TFunction,
): { value: string; color: string | undefined } => {
  const { pendingTrades, pendingTradesCalled, pendingTradesLoading } =
    pendingTradesState;

  const hasActivePendingTrades =
    pendingTradesCalled && !pendingTradesLoading && pendingTrades.length > 0;
  const hasStalePendingTrades =
    pendingTradesCalled &&
    !pendingTradesLoading &&
    pendingTrades.length === 0 &&
    report.pendingSubTradeRequests > 0;

  if (hasStalePendingTrades) {
    return {
      value: t('rowDetail.generalInfo.staleActiveTrades', {
        count: report.pendingSubTradeRequests,
      }),
      color: theme.color.warning,
    };
  }
  if (hasActivePendingTrades) {
    return {
      value: t('rowDetail.generalInfo.activeTradesCount', {
        count: pendingTrades.length,
      }),
      color: theme.color.warning,
    };
  }
  if (
    (!pendingTradesCalled || pendingTradesLoading) &&
    report.pendingSubTradeRequests > 0
  ) {
    return {
      value: t('rowDetail.generalInfo.activeTradesCount', {
        count: report.pendingSubTradeRequests,
      }),
      color: theme.color.warning,
    };
  }
  return {
    value: t('rowDetail.generalInfo.none'),
    color: theme.color.onSurfaceVariant,
  };
};

export function buildGeneralInfoRows(
  report: PortfolioReport,
  enrichedHoldings: EnrichedHolding[],
  theme: Theme,
  pendingTradesState: PendingTradesState,
  t: TFunction,
  formatMoney: FormatMoneyFn,
): GeneralInfoRow[] {
  const driftPct = calculateDrift(report);
  const pendingDisplay = getPendingTradesDisplay(
    report,
    pendingTradesState,
    theme,
    t,
  );
  const driftDollarSum = enrichedHoldings.reduce(
    (sum, h) => sum + Math.abs(h.driftCents),
    0,
  );
  const driftSeverity =
    driftPct !== null ? classifyDriftSeverity(driftPct, theme, t) : null;

  return [
    {
      label: t('rowDetail.generalInfo.marketValue'),
      value: formatMoney(report.marketValueCents),
      color: theme.color.onSurface,
    },
    {
      label: t('rowDetail.generalInfo.cash'),
      value: `${formatMoney(report.cashAvailableForTradeCents)} (${report.marketValueCents > 0 ? ((report.cashAvailableForTradeCents / report.marketValueCents) * 100).toFixed(1) : '0.0'}%)`,
      color: theme.color.onSurface,
    },
    {
      label: t('rowDetail.generalInfo.targetCash'),
      value: `${formatMoney(report.expectedCashCents || 0)} (${((report.expectedCashPercentage || 0) * 100).toFixed(1)}%)`,
      color: theme.color.onSurface,
    },
    {
      label: t('rowDetail.generalInfo.drift'),
      value:
        driftPct !== null && driftSeverity
          ? `${driftSeverity.label} · ${formatMoney(driftDollarSum)} (${driftPct.toFixed(1)}%)`
          : '-',
      color: driftSeverity?.color ?? theme.color.positive,
    },
    {
      label: t('rowDetail.generalInfo.pending'),
      value: pendingDisplay.value,
      color: pendingDisplay.color,
    },
    {
      label: t('rowDetail.generalInfo.holdings'),
      value: t('rowDetail.generalInfo.positionsCount', {
        count: report.holdings?.length || 0,
      }),
      color: theme.color.onSurface,
    },
  ];
}
